import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

import {
  isValidCatalog,
  savePass,
  selectPropertiesApp,
  selectPropertiesDownload,
  useLazyGetPropertiesQuery,
  useLoginMutation,
} from "../../redux";
import { useClearLoginInfo } from "../useClearLoginInfo";
import { PROPERTIES_URL, PROPERTY_KEYS } from "../../config";
import { toHttps } from "../../utils/serviceUrl";

export interface LoginFormFields {
  username: string;
  password: string;
  typeLogin: string;
}

const keyPrefix = "hooks.formManagements.useLoginFormManagement";

const defaultValues: LoginFormFields = {
  username: "",
  password: "",
  typeLogin: "1",
};

export const useLoginFormManagement = () => {
  const [fieldsValidationErr, setFieldsValidationErr] = useState<
    string | null
  >();
  const [genericErr, setGenericErr] = useState<string | null>();
  const { t } = useTranslation("translation", { keyPrefix });
  const dispatch = useDispatch();

  const [login, { isLoading }] = useLoginMutation();
  const [getProperties, { isFetching: isFetchingProperties }] =
    useLazyGetPropertiesQuery();
  const properties = useSelector(selectPropertiesApp);
  const propertiesDownload = useSelector(selectPropertiesDownload);
  const urlLogin = properties?.[PROPERTY_KEYS.LOGIN];
  /**
   * En una primera visita, o con una copia guardada sin la URL del ingreso,
   * «Ingresar» espera a que llegue el catálogo: presionarlo antes mostraba el
   * error de conexión aunque la red estuviera bien (VWA-43). Solo mientras la
   * descarga está en curso, que tiene tope de tiempo; si falla, se habilita
   * para que el envío reintente y, si tampoco llega, diga qué pasó.
   */
  const waitingProperties = !urlLogin && propertiesDownload.isLoading;
  /**
   * El catálogo llegó bien y aun así no trae la URL: volver a pedirlo daría lo
   * mismo, así que no se reintenta.
   */
  const catalogWithoutLogin =
    propertiesDownload.isSuccess && isValidCatalog(propertiesDownload.data);
  const { clearDataLogin } = useClearLoginInfo();

  const schema = yup.object().shape({
    username: yup.string().required(t("schema.username.required")),
    password: yup.string().required(t("schema.password.required")),
  });

  const methods = useForm<LoginFormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    const { formState: errors } = methods;
    setFieldsValidationErr(
      errors.errors.username?.message || errors.errors.password?.message || null
    );
    // eslint-disable-next-line
  }, [methods.formState.errors]);

  const submit: SubmitHandler<LoginFormFields> = async (fields) => {
    clearDataLogin();
    clearErrorMessages();

    /**
     * Sin esta guarda, `urlLogin.replace` lanza un `TypeError` sin capturar en
     * cuanto la propiedad falta — es el hallazgo E, el mismo patrón que causó
     * dos incidencias reales de «Ingresar» sin respuesta.
     */
    const url =
      urlLogin || (catalogWithoutLogin ? undefined : await fetchLoginUrl());
    if (!url) {
      setGenericErr(t("errors.connection"));
      return;
    }

    /**
     * El usuario pegado de un correo o un chat arrastra espacios que no se ven,
     * y cada envío así cuesta un intento (hallazgo F, VWA-35). Solo se recortan
     * los extremos: un espacio interno podría ser parte de un usuario real.
     */
    const credentials = { ...fields, username: fields.username.trim() };

    try {
      await login({ fields: credentials, url: toHttps(url) }).unwrap();
      dispatch(savePass(fields.password));
    } catch (err) {
      /**
       * Un rechazo con respuesta HTTP —credenciales, bloqueo, cuenta
       * pendiente— ya lo tradujo `authSlice` a `authError`, y el formulario lo
       * muestra en su propio modal. Avisar aquí además duplicaría el aviso.
       *
       * Sin `status` numérico no hay cuerpo que leer: es una falla de
       * transporte, y este es el único sitio que puede contarla.
       */
      const fetchError = err as FetchBaseQueryError | undefined;
      if (!fetchError || typeof fetchError.status !== "number") {
        setGenericErr(t("errors.apiError"));
      }
      /**
       * El `422` es la única respuesta fallida que conserva la contraseña: es
       * la temporal, y la pantalla de cambio la necesita —como hash— para
       * rechazar una contraseña nueva idéntica (HU-CUDL-089 / CA-08).
       */
      dispatch(
        savePass(fetchError?.status === 422 ? fields.password : null)
      );
    }
  };

  /**
   * Vuelve a pedir el catálogo una vez, cuando la URL del ingreso no está:
   * la descarga del arranque pudo fallar por un corte que ya pasó.
   *
   * @returns La URL del ingreso, o `undefined` si el catálogo tampoco llegó.
   */
  const fetchLoginUrl = async (): Promise<string | undefined> => {
    try {
      const response = await getProperties(PROPERTIES_URL).unwrap();
      return isValidCatalog(response)
        ? response.properties?.[PROPERTY_KEYS.LOGIN]
        : undefined;
    } catch {
      return undefined;
    }
  };

  const clearErrorMessages = (): void => {
    setGenericErr(null);
    setFieldsValidationErr(null);
  };

  return {
    clearErrorMessages,
    loading: isLoading || isFetchingProperties,
    waitingProperties,
    defaultValues,
    fieldsValidationErr,
    genericErr,
    methods,
    submit,
  };
};
